import { isCosme } from '@/business_specification/20220523-cosme';
import { isFancl } from '@/business_specification/20220523-fancl';
import { isPeachJohn } from '@/business_specification/20260312-peachJohn';
import { IS_DEV_STAGE, SDK_TOKEN } from '@/js/constants';
import HREF_TARGET, { type HrefTargetType } from '@/js/hrefTarget';
import { tre } from '@/plugins/bugsnag';
import { globalConfig } from '@/store/globalConfig';

import { postFastCheckout } from './api';

declare global {
  // eslint-disable-next-line no-unused-vars
  interface Window {
    HUT?: {
      appendCrossDomainLinkingUrlParameter: (url: string) => string;
    };
  }
}

const isForQA = (): boolean => {
  if (IS_DEV_STAGE) {
    return [
      'q6oyld2w', // dev: 1844
      '325m3061', // dev: 1907 Andy 桑
    ].includes(SDK_TOKEN);
  }

  return false;
};

const isSpecialStoreOfOpeningInTheSamePage = (): boolean => {
  if (isForQA()) { return true; }
  if (isFancl()) { return true; }
  if (isCosme()) { return true; }
  if (isPeachJohn()) { return true; }

  return false;
};

// ref: https://blog.user.today/inapp-browser-webview-problem/
// In LINE webview, window.open will replace original page
const isInLineWebview = (): boolean => /Line/i.test(navigator.userAgent);

const getOpenWindowType = (): HrefTargetType => {
  if (globalConfig.value.openWindowType) { 
    return globalConfig.value.openWindowType as HrefTargetType; 
  }

  if (isSpecialStoreOfOpeningInTheSamePage() || isInLineWebview()) {
    return HREF_TARGET.SAME_PAGE;
  }

  return HREF_TARGET.NEW_PAGE;
};

export const openNewWindowAfterFastCheckout = async (
  postId: number, 
  productId: number
): Promise<void> => {
  const getTargetLink = async (): Promise<string | undefined> => {
    const result = await postFastCheckout(postId, productId);
    if (!result.ok) { return; }
    const redirectUrl = result.data.redirect_url;
    return window?.HUT?.appendCrossDomainLinkingUrlParameter(redirectUrl) ?? redirectUrl;
  };

  // 取得開啟的類型
  const openWindowType = getOpenWindowType();

  try {
    // 開新分頁 For Web
    if (openWindowType === HREF_TARGET.NEW_PAGE) {
      // ref: https://stackoverflow.com/a/39387533
      // window.open() **BEFORE** API to solve iOS safari problem
      const winRef = window.open('about:blank', '_blank');
      const targetLink = await getTargetLink();
      if (targetLink && winRef) {
        winRef.location.href = targetLink;
      }

      return;
    }

    // 原頁取代
    if (openWindowType === HREF_TARGET.SAME_PAGE) {
      const targetLink = await getTargetLink();
      if (targetLink) {
        window.open(targetLink, '_self');
      }
    }
  } catch (ex) {
    tre(ex);
  }
};

export const $el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attr?: Partial<HTMLElementTagNameMap[K]>,
  cb?: (el: HTMLElementTagNameMap[K]) => void
): HTMLElementTagNameMap[K] => {
  const el = document.createElement(tag);
  if (attr) {
    Object.assign(el, attr);
  }
  cb?.(el);
  return el;
};

export const $ = <K extends keyof HTMLElementTagNameMap>(
  selectors: string
): HTMLElementTagNameMap[K] | null => {
  return document.querySelector(selectors);
};
